import { SagaIterator } from 'redux-saga'
import * as effects from 'redux-saga/effects'
import * as api from './api'
import * as actions from './actions'
import { formatErrorMessage } from '../../utils/helpers'
import * as Sentry from '@sentry/react'
import { DataCreateBlockType, UpdateBlockDataType } from './types'

function* fetchModuleScreenBlocks(payload: { sid: string }): SagaIterator {
  try {
    const moduleScreen = yield effects.call(api.fetchModuleScreen, payload.sid)
    yield effects.put(
      actions.fetchModuleScreenBlocksSuccess(moduleScreen.data.blocks)
    )
  } catch (error) {
    console.warn('Cannot fetch module screen blocks', error)
    yield effects.put(
      actions.fetchModuleScreenBlocksFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* updateBlock(payload: {
  sid: string
  block_id: string
  data: UpdateBlockDataType
}): SagaIterator {
  try {
    const { sid, block_id, data } = payload
    yield effects.call(api.updateBlock, block_id, data)
    yield effects.put(actions.updateBlockSuccess())
    yield effects.put(actions.fetchModuleScreenBlocks(sid))
  } catch (error) {
    console.warn('Cannot update block', error)
    yield effects.put(actions.updateBlockFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* sortModuleScreenBlocks(payload: {
  sid: string
  ids: string[]
}): SagaIterator {
  try {
    const { sid, ids } = payload
    yield effects.call(api.sortModuleScreenBlocks, sid, ids)
    yield effects.put(actions.sortModuleScreenBlocksSuccess())
    yield effects.put(actions.fetchModuleScreenBlocks(sid))
  } catch (error) {
    console.warn('Cannot sort blocks', error)
    yield effects.put(
      actions.sortModuleScreenBlocksFailure(formatErrorMessage(error))
    )
    Sentry.captureException(error)
  }
}

function* deleteBlock(payload: {
  sid: string
  block_id: string
}): SagaIterator {
  try {
    const { sid, block_id } = payload
    yield effects.call(api.deleteBlock, block_id)
    yield effects.put(actions.deleteBlockSuccess())
    yield effects.put(actions.fetchModuleScreenBlocks(sid))
  } catch (error) {
    console.warn('Cannot delete block', error)
    yield effects.put(actions.deleteBlockFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* createBlock(payload: {
  sid: string
  data: DataCreateBlockType
  position: number
}): SagaIterator {
  try {
    const { sid, data, position } = payload
    const block = yield effects.call(api.createBlock, data)
    yield effects.call(
      api.attachModuleScreenBlock,
      sid,
      block.data.block_id,
      position
    )
    yield effects.put(actions.fetchModuleScreenBlocks(sid))
    yield effects.put(actions.createBlockSuccess())
  } catch (error) {
    console.warn('Cannot create block', error)
    yield effects.put(actions.createBlockFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* fetchNotion(payload: {
  block_id: string
  notion_page_id: string
}): SagaIterator {
  try {
    const { block_id, notion_page_id } = payload
    yield effects.call(api.fetchNotion, block_id, notion_page_id)
    yield effects.put(actions.fetchNotionSuccess())
  } catch (error) {
    console.warn('Cannot fetch notion', error)
    yield effects.put(actions.fetchNotionFailure(formatErrorMessage(error)))
    Sentry.captureException(error)
  }
}

function* watchFetchModuleScreenBlocks(): SagaIterator {
  yield effects.takeEvery(
    actions.fetchModuleScreenBlocks.toString(),
    fetchModuleScreenBlocks
  )
}

function* watchUpdateBlock(): SagaIterator {
  yield effects.takeEvery(actions.updateBlock.toString(), updateBlock)
}

function* watchSortModuleScreenBlocks(): SagaIterator {
  yield effects.takeEvery(
    actions.sortModuleScreenBlocks.toString(),
    sortModuleScreenBlocks
  )
}

function* watchDeleteBlock(): SagaIterator {
  yield effects.takeEvery(actions.deleteBlock.toString(), deleteBlock)
}

function* watchCreateBlock(): SagaIterator {
  yield effects.takeEvery(actions.createBlock.toString(), createBlock)
}

function* watchFetchNotion(): SagaIterator {
  yield effects.takeEvery(actions.fetchNotion.toString(), fetchNotion)
}

export default function* watchAdminModuleScreenBlocks(): SagaIterator {
  yield effects.all([
    effects.fork(watchFetchModuleScreenBlocks),
    effects.fork(watchUpdateBlock),
    effects.fork(watchSortModuleScreenBlocks),
    effects.fork(watchDeleteBlock),
    effects.fork(watchCreateBlock),
    effects.fork(watchFetchNotion),
  ])
}
